import React, { useEffect, useState } from 'react';
import SEO from '../../components/SEO';
import PageLoader from '../../components/PageLoader';
import { Link, useNavigate } from 'react-router-dom';
import api from '../../utils/api';
import AdminLayout from '../../components/admin/AdminLayout';
import { useSidebar } from '../../contexts/SidebarContext';
import { showSuccess, showError, showConfirm } from '../../utils/toast';
import Pagination from '../../components/Pagination';
import { ChevronDown, ChevronUp, Calendar, X, MoreVertical, ChevronRight, CheckCircle, XCircle, Trash2, Star, Pencil } from 'lucide-react';

const AdminReportsContent = () => {
  const { isCollapsed } = useSidebar();
  const navigate = useNavigate();
  const [reports, setReports] = useState([]);
  const [loading, setLoading] = useState(true);
  const [statusFilter, setStatusFilter] = useState('all');
  const [pagination, setPagination] = useState(null);
  const [currentPage, setCurrentPage] = useState(1);
  const [expandedRows, setExpandedRows] = useState(new Set());
  const [dateFilter, setDateFilter] = useState('all');
  const [customStartDate, setCustomStartDate] = useState('');
  const [customEndDate, setCustomEndDate] = useState('');
  const [showDateFilter, setShowDateFilter] = useState(false);
  const [sort, setSort] = useState('newest');
  const [limit, setLimit] = useState(20);
  const [searchInput, setSearchInput] = useState('');
  const [search, setSearch] = useState('');

  useEffect(() => {
    if (statusFilter !== 'all' || dateFilter !== 'all' || sort !== 'newest' || limit !== 20) setCurrentPage(1);
  }, [statusFilter, dateFilter, sort, limit]);

  useEffect(() => {
    fetchReports();
  }, [statusFilter, currentPage, dateFilter, customStartDate, customEndDate, sort, limit, search]);

  const getDateRange = () => {
    if (dateFilter === 'all') return {};
    const now = new Date();
    let startDate = null, endDate = null;
    switch (dateFilter) {
      case 'today':
        startDate = new Date(now); startDate.setHours(0, 0, 0, 0);
        endDate = new Date(now); endDate.setHours(23, 59, 59, 999);
        break;
      case 'yesterday':
        const yesterday = new Date(now); yesterday.setDate(yesterday.getDate() - 1);
        startDate = new Date(yesterday); startDate.setHours(0, 0, 0, 0);
        endDate = new Date(yesterday); endDate.setHours(23, 59, 59, 999);
        break;
      case 'lastWeek':
        const lastWeek = new Date(now); lastWeek.setDate(lastWeek.getDate() - 7);
        startDate = new Date(lastWeek); startDate.setHours(0, 0, 0, 0);
        endDate = new Date(now); endDate.setHours(23, 59, 59, 999);
        break;
      case 'lastMonth':
        const lastMonth = new Date(now); lastMonth.setMonth(lastMonth.getMonth() - 1);
        startDate = new Date(lastMonth); startDate.setHours(0, 0, 0, 0);
        endDate = new Date(now); endDate.setHours(23, 59, 59, 999);
        break;
      case 'custom':
        if (customStartDate) { startDate = new Date(customStartDate); startDate.setHours(0, 0, 0, 0); }
        if (customEndDate) { endDate = new Date(customEndDate); endDate.setHours(23, 59, 59, 999); }
        break;
      default:
        return {};
    }
    const params = {};
    if (startDate) params.startDate = startDate.toISOString();
    if (endDate) params.endDate = endDate.toISOString();
    return params;
  };

  const fetchReports = async () => {
    try {
      setLoading(true);
      const dateParams = getDateRange();
      const sortParam = sort === 'oldest' ? 'createdAt:asc' : 'createdAt:desc';
      const params = {
        ...(statusFilter !== 'all' && { status: statusFilter }),
        ...dateParams,
        ...(search && { search }),
        page: currentPage,
        limit,
        sort: sortParam
      };
      const response = await api.get('/reports', { params });
      if (response.data?.reports) {
        setReports(response.data.reports);
        setPagination(response.data.pagination);
      } else {
        setReports([]);
        setPagination(null);
      }
    } catch (error) {
      console.error('Error fetching reports:', error);
      setReports([]);
      setPagination(null);
    } finally {
      setLoading(false);
    }
  };

  const handleStatusChange = async (id, newStatus) => {
    try {
      await api.patch(`/reports/${id}/status`, { status: newStatus });
      fetchReports();
      showSuccess(`Report ${newStatus} successfully!`);
    } catch (error) {
      showError(error.response?.data?.message || 'Error updating status');
    }
  };

  const handleDelete = async (id) => {
    showConfirm('Are you sure you want to delete this report? This action cannot be undone.', async () => {
      try {
        await api.delete(`/reports/${id}`);
        fetchReports();
        showSuccess('Report deleted successfully!');
      } catch (error) {
        showError(error.response?.data?.message || 'Error deleting report');
      }
    });
  };

  // Quick actions on the reported business
  const businessId = (r) => r.business?._id || r.business;

  const handleToggleFeatured = (r) => {
    const id = businessId(r);
    if (!id) return;
    const feat = r.business?.featured;
    if (feat) {
      showConfirm(
        'Do you really want to remove this business from favourites? It will no longer appear in the featured section on the home page.',
        async () => {
          try {
            await api.put(`/businesses/${id}`, { featured: false });
            fetchReports();
            showSuccess('Removed from favourites!');
          } catch (err) {
            showError(err.response?.data?.message || 'Error updating');
          }
        }
      );
    } else {
      (async () => {
        try {
          await api.put(`/businesses/${id}`, { featured: true });
          fetchReports();
          showSuccess('Added to favourites!');
        } catch (err) {
          showError(err.response?.data?.message || 'Error updating');
        }
      })();
    }
  };

  const handleApproveBusiness = async (id) => {
    try {
      await api.patch(`/businesses/${id}/status`, { status: 'approved' });
      fetchReports();
      showSuccess('Business approved successfully!');
    } catch (err) {
      showError(err.response?.data?.message || 'Error approving business');
    }
  };

  const handleRejectBusiness = async (id) => {
    try {
      await api.patch(`/businesses/${id}/status`, { status: 'rejected' });
      fetchReports();
      showSuccess('Business rejected successfully!');
    } catch (err) {
      showError(err.response?.data?.message || 'Error rejecting business');
    }
  };

  const handleRemoveListing = (id) => {
    showConfirm(
      'Do you really want to remove this listing? The business will be permanently deleted. This action cannot be undone.',
      async () => {
        try {
          await api.delete(`/businesses/${id}`);
          fetchReports();
          showSuccess('Listing removed successfully!');
        } catch (err) {
          showError(err.response?.data?.message || 'Error removing listing');
        }
      }
    );
  };

  const toggleExpand = (id) => {
    setExpandedRows(prev => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  };

  return (
    <>
      <SEO title="Manage Reports - Admin" description="Manage reports." noindex />
      <main className={`flex-1 transition-all duration-300 ${isCollapsed ? 'ml-16' : 'ml-64'}`}>
        <div className="container-custom py-6 md:py-12">
          <div className="mb-6 md:mb-8">
            <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4 mb-4">
              <h1 className="text-2xl md:text-3xl lg:text-4xl font-bold text-gray-800 dark:text-gray-50">Manage Reports</h1>
            </div>
            <div className="mb-4 flex flex-col sm:flex-row gap-4 flex-wrap">
              <div className="flex flex-1 flex-wrap gap-2 min-w-[200px]">
                <input
                  type="text"
                  value={searchInput}
                  onChange={(e) => setSearchInput(e.target.value)}
                  onKeyDown={(e) => {
                    if (e.key === 'Enter') {
                      setSearch(searchInput);
                      setCurrentPage(1);
                    }
                  }}
                  placeholder="Search by reason, comment, business, user..."
                  className="flex-1 min-w-[180px] px-4 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-800 text-gray-900 dark:text-gray-50 focus:outline-none focus:ring-2 focus:ring-primary-500 text-sm"
                />
                <button
                  onClick={() => { setSearch(searchInput); setCurrentPage(1); }}
                  className="px-4 py-2 bg-primary-600 text-white rounded-lg hover:bg-primary-700 transition-colors text-sm font-medium"
                >
                  Search
                </button>
                {(searchInput || search) && (
                  <button
                    onClick={() => { setSearchInput(''); setSearch(''); setCurrentPage(1); }}
                    className="px-4 py-2 bg-gray-200 dark:bg-gray-700 text-gray-700 dark:text-gray-300 rounded-lg hover:bg-gray-300 dark:hover:bg-gray-600 transition-colors text-sm font-medium"
                  >
                    Clear
                  </button>
                )}
              </div>
              <select value={statusFilter} onChange={(e) => { setStatusFilter(e.target.value); setLoading(true); }} className="input-field sm:w-auto">
                <option value="all">All Status</option>
                <option value="pending">Pending</option>
                <option value="resolved">Resolved</option>
                <option value="dismissed">Dismissed</option>
              </select>
              <div className="flex items-center gap-4">
                <div className="flex items-center gap-2">
                  <label className="text-sm font-medium text-gray-700 dark:text-gray-300">Sort:</label>
                  <select value={sort} onChange={(e) => { setSort(e.target.value); setCurrentPage(1); }} className="px-4 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-800 text-gray-900 dark:text-gray-50 text-sm">
                    <option value="newest">Newest first</option>
                    <option value="oldest">Oldest first</option>
                  </select>
                </div>
                <div className="flex items-center gap-2">
                  <label className="text-sm font-medium text-gray-700 dark:text-gray-300">Entries per page:</label>
                  <select value={limit} onChange={(e) => { setLimit(Number(e.target.value)); setCurrentPage(1); }} className="px-4 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-800 text-gray-900 dark:text-gray-50 text-sm">
                    {[5, 10, 20, 50].map((n) => <option key={n} value={n}>{n}</option>)}
                  </select>
                </div>
              </div>
              <button
                onClick={() => setShowDateFilter(!showDateFilter)}
                className={`flex items-center gap-2 px-4 py-2 rounded-lg text-sm font-medium transition-colors whitespace-nowrap ${showDateFilter || dateFilter !== 'all' ? 'bg-primary-600 text-white' : 'bg-gray-100 dark:bg-gray-800 text-gray-700 dark:text-gray-300 hover:bg-gray-200 dark:hover:bg-gray-700'}`}
              >
                <Calendar className="w-4 h-4" />
                <span className="hidden sm:inline">Date Filter</span>
                {showDateFilter ? <ChevronUp className="w-4 h-4" /> : <ChevronDown className="w-4 h-4" />}
                {dateFilter !== 'all' && <span className="bg-white/20 px-1.5 py-0.5 rounded text-xs font-semibold">1</span>}
              </button>
            </div>

            {showDateFilter && (
              <div className="card p-4 md:p-6 mb-4 animate-slide-down">
                <div className="flex items-center justify-between mb-4">
                  <h3 className="text-lg font-semibold text-gray-900 dark:text-gray-50 flex items-center gap-2"><Calendar className="w-5 h-5" />Date Filter</h3>
                  <button onClick={() => setShowDateFilter(false)} className="p-1.5 hover:bg-gray-100 dark:hover:bg-gray-700 rounded-lg" aria-label="Close"><X className="w-5 h-5 text-gray-500" /></button>
                </div>
                <div className="space-y-4">
                  <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
                    {['today', 'yesterday', 'lastWeek', 'lastMonth'].map((key) => (
                      <button key={key} onClick={() => { setDateFilter(key); setCustomStartDate(''); setCustomEndDate(''); }} className={`px-4 py-2 rounded-lg text-sm font-medium ${dateFilter === key ? 'bg-primary-600 text-white' : 'bg-gray-100 dark:bg-gray-800 text-gray-700 dark:text-gray-300'}`}>
                        {key === 'lastWeek' ? 'Last Week' : key === 'lastMonth' ? 'Last Month' : key.charAt(0).toUpperCase() + key.slice(1)}
                      </button>
                    ))}
                  </div>
                  <div>
                    <button onClick={() => setDateFilter('custom')} className={`px-4 py-2 rounded-lg text-sm font-medium mb-3 ${dateFilter === 'custom' ? 'bg-primary-600 text-white' : 'bg-gray-100 dark:bg-gray-800 text-gray-700 dark:text-gray-300'}`}>Custom Range</button>
                    {dateFilter === 'custom' && (
                      <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                        <div><label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">Start Date</label><input type="date" value={customStartDate} onChange={(e) => setCustomStartDate(e.target.value)} className="input-field text-sm w-full" /></div>
                        <div><label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">End Date</label><input type="date" value={customEndDate} onChange={(e) => setCustomEndDate(e.target.value)} className="input-field text-sm w-full" /></div>
                      </div>
                    )}
                  </div>
                  {dateFilter !== 'all' && (
                    <div className="pt-3 border-t border-gray-200 dark:border-gray-700">
                      <button onClick={() => { setDateFilter('all'); setCustomStartDate(''); setCustomEndDate(''); }} className="text-sm text-primary-600 dark:text-primary-400 font-medium flex items-center gap-2"><X className="w-4 h-4" /> Clear Date Filter</button>
                    </div>
                  )}
                </div>
              </div>
            )}
          </div>

          {loading ? (
            <PageLoader message="Loading reports..." />
          ) : (
            <>
              <div className="hidden lg:block card overflow-hidden">
                <div className="overflow-x-auto">
                  <table className="w-full">
                    <thead className="bg-gray-50 dark:bg-gray-800">
                      <tr>
                        <th className="px-4 md:px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase w-14">Sr. No.</th>
                        <th className="px-4 md:px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase">Business</th>
                        <th className="px-4 md:px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase">User</th>
                        <th className="px-4 md:px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase">Reason</th>
                        <th className="px-4 md:px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase">Comment</th>
                        <th className="px-4 md:px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase">Status</th>
                        <th className="px-4 md:px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase">Actions</th>
                      </tr>
                    </thead>
                    <tbody className="divide-y divide-gray-200 dark:divide-gray-700 bg-white dark:bg-gray-800">
                      {reports.map((report, index) => {
                        const isExpanded = expandedRows.has(report._id);
                        const srNo = (currentPage - 1) * limit + index + 1;
                        return (
                          <React.Fragment key={report._id}>
                            <tr className="hover:bg-gray-50 dark:hover:bg-gray-700 transition-colors">
                              <td className="px-4 md:px-6 py-4 whitespace-nowrap text-gray-600 dark:text-gray-400 text-sm">{srNo}</td>
                              <td className="px-4 md:px-6 py-4">
                                <div className="font-semibold text-gray-900 dark:text-gray-50">{report.business?.name || 'N/A'}</div>
                                {typeof report.businessReportCount === 'number' && <div className="text-xs text-gray-600 dark:text-gray-400 mt-0.5">{report.businessReportCount} {report.businessReportCount === 1 ? 'report' : 'reports'} against this business</div>}
                                {report.business?.slug && report.business?.category?.slug && <Link to={`/${report.business.category.slug}/${report.business.slug}`} className="text-xs text-primary-600 dark:text-primary-400 hover:underline">View business</Link>}
                              </td>
                              <td className="px-4 md:px-6 py-4"><div className="font-semibold text-gray-900 dark:text-gray-50">{report.user?.name || 'N/A'}</div><div className="text-sm text-gray-500 dark:text-gray-400">{report.user?.email || ''}</div></td>
                              <td className="px-4 md:px-6 py-4 text-sm text-gray-700 dark:text-gray-300">{report.reason}</td>
                              <td className="px-4 md:px-6 py-4 max-w-md"><p className="text-sm text-gray-700 dark:text-gray-300 line-clamp-2">{report.comment}</p></td>
                              <td className="px-4 md:px-6 py-4"><span className={`badge text-xs ${report.status === 'resolved' ? 'badge-success' : report.status === 'dismissed' ? 'bg-gray-100 dark:bg-gray-700 text-gray-700 dark:text-gray-300' : 'badge-warning'}`}>{report.status}</span></td>
                              <td className="px-4 md:px-6 py-4">
                                <button onClick={() => toggleExpand(report._id)} className="p-2 hover:bg-gray-100 dark:hover:bg-gray-600 rounded-lg flex items-center gap-1" aria-label={isExpanded ? 'Collapse' : 'Expand'} title={isExpanded ? 'Collapse' : 'Full comment & actions'}><MoreVertical className="w-4 h-4 text-gray-600 dark:text-gray-400" />{isExpanded ? <ChevronUp className="w-4 h-4" /> : <ChevronRight className="w-4 h-4" />}</button>
                              </td>
                            </tr>
                            {isExpanded && (
                              <tr className="bg-gray-50 dark:bg-gray-900/50">
                                <td colSpan="7" className="px-4 md:px-6 py-4">
                                  <div className="mb-3"><span className="text-sm font-semibold text-gray-700 dark:text-gray-300">Full comment: </span><span className="text-sm text-gray-600 dark:text-gray-400">{report.comment}</span></div>
                                  <div className="flex flex-wrap gap-2">
                                    {report.status !== 'resolved' && (
                                      <button onClick={() => { handleStatusChange(report._id, 'resolved'); setExpandedRows(prev => { const s = new Set(prev); s.delete(report._id); return s; }); }} className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-sm font-medium bg-success-600 text-white hover:bg-success-700 transition-colors" title="Resolve">
                                        <CheckCircle className="w-4 h-4" />
                                        Resolve
                                      </button>
                                    )}
                                    <button
                                      onClick={() => { if (report.status !== 'dismissed') { handleStatusChange(report._id, 'dismissed'); setExpandedRows(prev => { const s = new Set(prev); s.delete(report._id); return s; }); } }}
                                      disabled={report.status === 'dismissed'}
                                      className={`inline-flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-sm font-medium transition-colors ${report.status === 'dismissed' ? 'bg-gray-300 dark:bg-gray-600 text-gray-500 dark:text-gray-400 cursor-not-allowed' : 'bg-gray-600 text-white hover:bg-gray-700 dark:bg-gray-500 dark:hover:bg-gray-600'}`}
                                      title={report.status === 'dismissed' ? 'Already dismissed' : 'Dismiss'}
                                    >
                                      <XCircle className="w-4 h-4" />
                                      Dismiss
                                    </button>
                                    <button onClick={() => { handleDelete(report._id); setExpandedRows(prev => { const s = new Set(prev); s.delete(report._id); return s; }); }} className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-sm font-medium bg-error-600 text-white hover:bg-error-700 transition-colors" title="Delete report">
                                      <Trash2 className="w-4 h-4" />
                                      Delete
                                    </button>
                                  </div>
                                  {businessId(report) && (
                                    <>
                                      <div className="mt-4 pt-4 border-t border-gray-200 dark:border-gray-700">
                                        <p className="text-sm font-semibold text-gray-700 dark:text-gray-300 mb-2">Quick actions on reported business</p>
                                        <div className="flex flex-wrap gap-2">
                                          <button onClick={() => handleToggleFeatured(report)} className="inline-flex items-center gap-1.5 text-sm font-medium px-2 py-1 rounded hover:bg-amber-50 dark:hover:bg-amber-900/20 transition-colors" title={report.business?.featured ? 'Remove from favourites' : 'Add to favourites'}>
                                            <Star className={`w-4 h-4 ${report.business?.featured ? 'text-amber-500 fill-amber-500' : 'text-gray-400 dark:text-gray-500'}`} />
                                            <span className={report.business?.featured ? 'text-amber-600 dark:text-amber-400' : 'text-gray-600 dark:text-gray-400'}>{report.business?.featured ? 'Remove from Favourites' : 'Add to Favourites'}</span>
                                          </button>
                                          <button onClick={() => navigate('/admin/businesses', { state: { editBusinessId: businessId(report) } })} className="inline-flex items-center gap-1.5 text-primary-600 dark:text-primary-400 hover:text-primary-700 dark:hover:text-primary-300 text-sm font-medium px-2 py-1 rounded hover:bg-primary-50 dark:hover:bg-primary-900/20 transition-colors" title="Edit business">
                                            <Pencil className="w-4 h-4" />
                                            Edit
                                          </button>
                                          {report.business?.status !== 'approved' && (
                                            <button onClick={() => handleApproveBusiness(businessId(report))} className="inline-flex items-center gap-1.5 text-success-600 dark:text-success-400 hover:text-success-700 dark:hover:text-success-300 text-sm font-medium px-2 py-1 rounded hover:bg-success-50 dark:hover:bg-success-900/20 transition-colors" title="Approve business">Approve</button>
                                          )}
                                          {report.business?.status !== 'rejected' && (
                                            <button onClick={() => handleRejectBusiness(businessId(report))} className="inline-flex items-center gap-1.5 text-error-600 dark:text-error-400 hover:text-error-700 dark:hover:text-error-300 text-sm font-medium px-2 py-1 rounded hover:bg-error-50 dark:hover:bg-error-900/20 transition-colors" title="Reject business">Reject</button>
                                          )}
                                          <button onClick={() => handleRemoveListing(businessId(report))} className="inline-flex items-center gap-1.5 text-error-600 dark:text-error-400 hover:text-error-700 dark:hover:text-error-300 text-sm font-medium px-2 py-1 rounded hover:bg-error-50 dark:hover:bg-error-900/20 transition-colors" title="Remove listing (permanent delete)">
                                            <Trash2 className="w-4 h-4" />
                                            Remove listing
                                          </button>
                                        </div>
                                      </div>
                                    </>
                                  )}
                                </td>
                              </tr>
                            )}
                          </React.Fragment>
                        );
                      })}
                    </tbody>
                  </table>
                </div>
              </div>

              <div className="lg:hidden space-y-4">
                {reports.map((report, index) => {
                  const isExpanded = expandedRows.has(report._id);
                  const srNo = (currentPage - 1) * limit + index + 1;
                  return (
                    <div key={report._id} className="card p-4 md:p-6">
                      <div className="flex items-start justify-between mb-2">
                        <div className="flex-1">
                          <div className="flex items-center gap-2 mb-1">
                            <span className="text-sm font-medium text-gray-500 dark:text-gray-400">{srNo}.</span>
                            <h3 className="font-bold text-gray-900 dark:text-gray-50">{report.business?.name || 'N/A'}</h3>
                          </div>
                          {typeof report.businessReportCount === 'number' && <p className="text-xs text-gray-600 dark:text-gray-400 mt-0.5">{report.businessReportCount} {report.businessReportCount === 1 ? 'report' : 'reports'} against this business</p>}
                          <p className="text-sm text-gray-600 dark:text-gray-400 mt-1">{report.user?.name || 'N/A'} • {report.reason}</p>
                        </div>
                        <button onClick={() => toggleExpand(report._id)} className="p-2 hover:bg-gray-100 dark:hover:bg-gray-700 rounded-lg">{isExpanded ? <ChevronUp className="w-5 h-5" /> : <ChevronRight className="w-5 h-5" />}</button>
                      </div>
                      <p className="text-sm text-gray-700 dark:text-gray-300 mb-3 line-clamp-2">{report.comment}</p>
                      <span className={`badge text-xs ${report.status === 'resolved' ? 'badge-success' : report.status === 'dismissed' ? 'bg-gray-100 dark:bg-gray-700 text-gray-700 dark:text-gray-300' : 'badge-warning'}`}>{report.status}</span>
                      {isExpanded && (
                        <div className="pt-3 border-t border-gray-200 dark:border-gray-700 mt-3">
                          <p className="text-sm font-semibold text-gray-700 dark:text-gray-300">Full comment:</p>
                          <p className="text-sm text-gray-600 dark:text-gray-400 mt-1 mb-3">{report.comment}</p>
                          <div className="flex flex-wrap gap-2">
                            {report.status !== 'resolved' && (
                              <button onClick={() => { handleStatusChange(report._id, 'resolved'); setExpandedRows(prev => { const s = new Set(prev); s.delete(report._id); return s; }); }} className="inline-flex items-center gap-1.5 btn-outline text-sm py-1.5 px-3 text-success-600 dark:text-success-400 border-success-600 dark:border-success-400" title="Resolve">
                                <CheckCircle className="w-4 h-4" />
                                Resolve
                              </button>
                            )}
                            <button
                              onClick={() => { if (report.status !== 'dismissed') { handleStatusChange(report._id, 'dismissed'); setExpandedRows(prev => { const s = new Set(prev); s.delete(report._id); return s; }); } }}
                              disabled={report.status === 'dismissed'}
                              className={`inline-flex items-center gap-1.5 btn-outline text-sm py-1.5 px-3 ${report.status === 'dismissed' ? 'opacity-60 cursor-not-allowed' : ''}`}
                              title={report.status === 'dismissed' ? 'Already dismissed' : 'Dismiss'}
                            >
                              <XCircle className="w-4 h-4" />
                              Dismiss
                            </button>
                            <button onClick={() => { handleDelete(report._id); setExpandedRows(prev => { const s = new Set(prev); s.delete(report._id); return s; }); }} className="inline-flex items-center gap-1.5 btn-outline text-sm py-1.5 px-3 text-error-600 dark:text-error-400 border-error-600 dark:border-error-400" title="Delete report">
                              <Trash2 className="w-4 h-4" />
                              Delete
                            </button>
                          </div>
                          {businessId(report) && (
                            <div className="mt-4 pt-4 border-t border-gray-200 dark:border-gray-700">
                              <p className="text-sm font-semibold text-gray-700 dark:text-gray-300 mb-2">Quick actions on reported business</p>
                              <div className="flex flex-wrap gap-2">
                                <button onClick={() => handleToggleFeatured(report)} className="inline-flex items-center gap-1.5 btn-outline text-sm py-1.5 px-3" title={report.business?.featured ? 'Remove from favourites' : 'Add to favourites'}>
                                  <Star className={`w-4 h-4 ${report.business?.featured ? 'text-amber-500 fill-amber-500' : 'text-gray-400'}`} />
                                  {report.business?.featured ? 'Remove from Favourites' : 'Add to Favourites'}
                                </button>
                                <button onClick={() => navigate('/admin/businesses', { state: { editBusinessId: businessId(report) } })} className="inline-flex items-center gap-1.5 btn-outline text-sm py-1.5 px-3 text-primary-600 dark:text-primary-400 border-primary-600 dark:border-primary-400" title="Edit business">
                                  <Pencil className="w-4 h-4" />
                                  Edit
                                </button>
                                {report.business?.status !== 'approved' && (
                                  <button onClick={() => handleApproveBusiness(businessId(report))} className="btn-outline text-sm py-1.5 px-3">Approve</button>
                                )}
                                {report.business?.status !== 'rejected' && (
                                  <button onClick={() => handleRejectBusiness(businessId(report))} className="btn-outline text-sm py-1.5 px-3 text-error-600 dark:text-error-400 border-error-600 dark:border-error-400">Reject</button>
                                )}
                                <button onClick={() => handleRemoveListing(businessId(report))} className="inline-flex items-center gap-1.5 btn-outline text-sm py-1.5 px-3 text-error-600 dark:text-error-400 border-error-600 dark:border-error-400" title="Remove listing">
                                  <Trash2 className="w-4 h-4" />
                                  Remove listing
                                </button>
                              </div>
                            </div>
                          )}
                        </div>
                      )}
                    </div>
                  );
                })}
              </div>

              {reports.length === 0 && (
                <div className="card p-12 text-center">
                  <div className="w-16 h-16 bg-gray-100 dark:bg-gray-800 rounded-full flex items-center justify-center mx-auto mb-4">
                    <svg className="w-8 h-8 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 21v-4m0 0V5a2 2 0 012-2h6.5l1 1H21l-3 6 3 6h-8.5l-1-1H5a2 2 0 00-2 2zm9-13.5V9" /></svg>
                  </div>
                  <p className="text-gray-600 dark:text-gray-400 text-lg">{search ? 'No reports match your search.' : 'No reports found.'}</p>
                </div>
              )}
            </>
          )}

          {pagination && pagination.totalPages >= 1 && (
            <div className="mt-6">
              <Pagination currentPage={pagination.currentPage} totalPages={pagination.totalPages} totalItems={pagination.totalItems} itemsPerPage={pagination.itemsPerPage} itemsOnCurrentPage={pagination.itemsOnCurrentPage} hasNextPage={pagination.hasNextPage} hasPrevPage={pagination.hasPrevPage} nextPage={pagination.nextPage} prevPage={pagination.prevPage} onPageChange={(p) => { setCurrentPage(p); window.scrollTo({ top: 0, behavior: 'smooth' }); }} />
            </div>
          )}
        </div>
      </main>
    </>
  );
};

const AdminReports = () => <AdminLayout><AdminReportsContent /></AdminLayout>;
export default AdminReports;
